////////////////////////////////////////////
// DEEPLINK RESUME — FINISH THE ACTION WHEN THE WALLET REDIRECTS BACK
////////////////////////////////////////////
//
// THE WALLET APP REDIRECTS TO OUR URL TAGGED ?dl=<kind> PLUS ITS RESPONSE
// PARAMS. captureDeeplinkReturn() RUNS SYNCHRONOUSLY AT BOOT (BEFORE THE
// ROUTER MOUNTS) TO STASH THOSE PARAMS AND SCRUB THE URL; useDeeplinkResume()
// (MOUNTED ONCE IN AuthProvider) THEN PAIRS THEM WITH THE PERSISTED PENDING
// RECORD AND FINISHES THE FLOW: STORE THE SESSION (connect), VERIFY THE
// COOKIE (signin), OR SUBMIT THE SIGNED TX (tx). OUTCOMES SURFACE THROUGH A
// TINY TOAST STORE PageShell RENDERS.
import { useEffect } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { postJson } from "@/lib/api/client";
import type { AuthMe } from "@/lib/api/types";
import { friendlyError } from "@/lib/sol-errors";
import * as log from "@/lib/log";
import { b58ToB64, decryptPayload, deriveShared } from "./crypto";
import { DL_PROVIDERS, DL_RETURN_PARAMS, type DlKind } from "./protocol";
import {
  clearDlSession, dlSession, saveDlSession, setHandoff, setSignInHint, sweepDl, tabId, takeHandoff, takePending,
  type DlHandoff,
} from "./store";

interface Captured {
  kind: DlKind;
  // ORIGINATING TAB (?dlt=) — WHOSE PENDING RECORD THIS RETURN RESUMES
  tab: string | null;
  params: Record<string, string>;
}

let captured: Captured | null = null;

////////////////////////////////////////////
// CAPTURE — SYNCHRONOUS, BEFORE THE ROUTER READS THE URL
////////////////////////////////////////////
export function captureDeeplinkReturn() {

  const here = new URL(location.href);
  const kind = here.searchParams.get("dl") as DlKind | null;
  if (!kind)
    return;

  const params: Record<string, string> = {};
  for (const p of DL_RETURN_PARAMS) {

    const v = here.searchParams.get(p);
    if (v !== null) params[p] = v;
    here.searchParams.delete(p);
  }
  captured = { kind, tab: params.dlt || null, params };
  // SCRUB — THE APP (AND ANY COPIED LINK) NEVER SEES THE WALLET'S PARAMS
  history.replaceState(null, "", here.pathname + here.search + here.hash);
}

////////////////////////////////////////////
// TOAST — MODULE-LEVEL SO A RESULT SURVIVES UNTIL PageShell MOUNTS
////////////////////////////////////////////

export interface DlToast {
  text: string;
  cls: "ok" | "err" | "info";
}

let toast: DlToast | null = null;
const toastListeners = new Set<() => void>();

function setToast(t: DlToast | null) {

  toast = t;
  for (const l of toastListeners) l();
}

export function dlToast(): DlToast | null {

  return toast;
}

export function dismissDlToast() {

  setToast(null);
}

export function subscribeDlToast(cb: () => void): () => void {

  toastListeners.add(cb);
  return () => { toastListeners.delete(cb); };
}

////////////////////////////////////////////
// RESUME HOOK — MOUNTED ONCE (AuthProvider); STRICTMODE-GUARDED
////////////////////////////////////////////

let resumed = false;

export function useDeeplinkResume() {

  const qc = useQueryClient();
  useEffect(() => {

    if (resumed)
      return;
    resumed = true;
    void processReturn(qc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

////////////////////////////////////////////
// DELIVER — LAND THE OUTCOME IN THE TAB THE USER SHOULD END UP IN. THE OS
// OFTEN OPENS THE WALLET'S REDIRECT IN A FRESH TAB (ANDROID VIEW INTENT);
// WHEN THAT HAPPENED, PARK THE OUTCOME FOR THE ORIGINATING TAB AND CLOSE
// THIS ONE (BROWSERS ALLOW SELF-CLOSE FOR EXTERNALLY-OPENED TABS) — CLOSING
// DROPS THE USER STRAIGHT BACK ONTO THEIR ORIGINAL TAB, WHOSE RETURN WATCHER
// (actions.ts) RELOADS/REDIRECTS AND CONSUMES THE HANDOFF. IF THE CLOSE IS
// BLOCKED (iOS SAFARI), FALL BACK TO FINISHING RIGHT HERE.
////////////////////////////////////////////
function deliver(foreign: boolean, originTab: string, toast: DlHandoff["toast"], redirect?: string) {

  if (!foreign) {

    if (redirect) {

      // PARK THE TOAST FOR THE DESTINATION LOAD — THE MODULE-LEVEL TOAST
      // STORE DIES WITH THIS DOCUMENT, AND THE OUTCOME MUST SURVIVE
      setHandoff({ toast }, originTab);
      location.assign(redirect);
      return;
    }
    setToast(toast);
    return;
  }

  setHandoff({ toast, redirect }, originTab);
  window.close();
  setTimeout(() => {

    takeHandoff(originTab);
    if (redirect) {

      setHandoff({ toast }, tabId());
      location.assign(redirect);
    } else {
      setToast(toast);
    }
  }, 400);
}

async function processReturn(qc: QueryClient) {

  // CLOSED-TAB LEFTOVERS — PER-TAB PENDING/HANDOFF KEYS EXPIRE VIA SWEEP
  sweepDl();

  if (!captured) {

    // NO WALLET RETURN ON THIS LOAD — BUT AN EARLIER DOCUMENT OF THIS TAB
    // (OR A FOREIGN RETURN TAB) MAY HAVE PARKED AN OUTCOME FOR US
    const h = takeHandoff();
    if (h)
      setToast(h.toast);
    return;
  }

  const cap = captured;
  captured = null;
  const pending = takePending(cap.tab ?? undefined);

  // A STALE REDIRECT (HISTORY REVISIT, DOUBLE TAP) HAS NO PENDING — IGNORE
  if (!pending || pending.kind !== cap.kind) {

    log.warn("wallet", "deeplink return without a matching pending action", { kind: cap.kind });
    return;
  }

  // DID THE REDIRECT LAND IN A FRESH TAB — OR BACK IN THE ORIGINATING ONE?
  const originTab = pending.tab ?? tabId();
  const foreign = originTab !== tabId();

  // WALLET REJECTED / ERRORED — THE REDIRECT CARRIES errorCode/errorMessage
  if (cap.params.errorCode) {

    const rejected = cap.params.errorCode === "4001";
    log.info("wallet", "deeplink " + pending.kind + (rejected ? " cancelled" : " failed"), {
      code: cap.params.errorCode, msg: cap.params.errorMessage,
    });
    // AN INVALIDATED SESSION NEEDS A FRESH CONNECT — DROP THE DEAD ONE
    if (/session/i.test(cap.params.errorMessage || ""))
      clearDlSession();
    deliver(foreign, originTab, rejected
      ? { text: "Cancelled in the wallet app.", cls: "info" }
      : { text: cap.params.errorMessage || "The wallet app returned an error.", cls: "err" });
    return;
  }

  try {

    if (pending.kind === "connect") {

      const meta = DL_PROVIDERS[pending.provider];
      const pek = cap.params[meta.pekParam];
      if (!pek || !cap.params.nonce || !cap.params.data)
        throw new Error("the wallet's response was incomplete");

      const shared = deriveShared(pek, pending.dappSecretKey);
      const res = decryptPayload<{ public_key: string; session: string }>(cap.params.data, cap.params.nonce, shared);
      saveDlSession({
        provider: pending.provider, net: pending.net,
        pubkey: res.public_key, session: res.session,
        sharedSecret: shared, dappPublicKey: pending.dappPublicKey,
      });
      // CONNECT-FOR-LOGIN — THE PICKER PROMPTS THE ONE REMAINING TAP
      if (pending.then === "signin")
        setSignInHint(pending.provider);
      log.info("wallet", "deeplink connected", { provider: pending.provider, net: pending.net });
      deliver(foreign, originTab, { text: meta.name + " connected ✓", cls: "ok" });
      return;
    }

    const session = dlSession();
    if (!session)
      throw new Error("the wallet session is gone — connect again");

    if (pending.kind === "signin") {

      const res = decryptPayload<{ signature: string }>(cap.params.data, cap.params.nonce, session.sharedSecret);
      const me = await postJson<AuthMe>("/api/auth/verify", {
        wallet: pending.wallet, nonce: pending.nonce, signature: b58ToB64(res.signature),
      });
      qc.setQueryData(["auth", "me"], me);
      log.info("wallet", "deeplink sign-in verified", { provider: pending.provider });
      deliver(foreign, originTab, { text: "Signed in ✓", cls: "ok" });
      return;
    }

    // TX — DECRYPT THE SIGNED TX AND HAND IT TO THE SERVER TO SUBMIT + CONFIRM
    const res = decryptPayload<{ transaction: string }>(cap.params.data, cap.params.nonce, session.sharedSecret);
    const done = await postJson<{ sig: string }>("/api/tx/submit", {
      txBase64: b58ToB64(res.transaction), net: pending.net,
    });
    log.info(pending.action, "confirmed (deeplink)", { sig: done.sig, net: pending.net });
    // FRESH DATA EVERYWHERE — THE WRITE LANDED WHILE THIS DOCUMENT WAS LOADING
    await qc.invalidateQueries();
    deliver(foreign, originTab, { text: pending.ok + " " + done.sig.slice(0, 12) + "…", cls: "ok" }, pending.successPath);
  } catch (e) {

    log.error("wallet", "deeplink resume failed", { kind: pending.kind, err: (e as Error)?.message });
    deliver(foreign, originTab, { text: friendlyError(e), cls: "err" });
  }
}
